import { useQuery } from "@tanstack/react-query";
import { invokeTracedFunction } from "@/lib/supabase-traced";
import { logger } from "@/lib/logger";

interface PublicConfig {
  googleMapsApiKey: string;
}

export const usePublicConfig = () => {
  return useQuery<PublicConfig>({
    queryKey: ['public-config'],
    queryFn: async () => {
      // Adicionar delay para evitar race condition no boot
      await new Promise(resolve => setTimeout(resolve, 100));
      
      logger.info('Fetching public config');
      
      try {
        const { data, error } = await invokeTracedFunction<PublicConfig>(
          'get-public-config',
          undefined,
          { timeout: 10000 }
        );
        
        if (error) {
          logger.error('Failed to fetch public config', error);
          throw error;
        }

        const keyLen = data?.googleMapsApiKey?.length || 0;
        logger.info('Public config fetched successfully', { keyLength: keyLen });

        if (!data?.googleMapsApiKey) {
          logger.warn('Google Maps API key is empty');
        }

        return data || { googleMapsApiKey: '' };
      } catch (e: any) {
        logger.error('Error fetching public config', e);
        
        // Fallback robusto para qualquer erro
        logger.warn('Using fallback empty config due to error');
        return { googleMapsApiKey: '' };
      }
    },
    staleTime: 1000 * 60 * 60, // 1 hour
    retry: 5,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
    refetchOnWindowFocus: false,
    refetchInterval: false,
    networkMode: 'online',
  });
};
